import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import {MatSelect} from '@angular/material/select';
import {NG_VALUE_ACCESSOR, ControlValueAccessor} from '@angular/forms';
import {SizeRange} from '../../shared/models/sizeRange';
import {WINDOW} from '../../shared/services/window.service';
import {isPlatformBrowser} from '@angular/common';
import {Subscription} from "rxjs";

const noop = () => {
};

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SizeSelectComponent),
  multi: true
};

@Component({
  selector: 'mbnm-size-select',
  templateUrl: './size-select.component.html',
  styleUrls: ['./size-select.component.scss'],
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class SizeSelectComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit, ControlValueAccessor {

  constructor(@Inject(WINDOW) private window: Window, @Inject(PLATFORM_ID) private platformId: Object) { }

  @Input()
  range: SizeRange;

  @Input()
  extraOptions:Array<any>;

  sizes:Array<any>;

  @Output() onClose:EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('select')
  public select:MatSelect;

  public fractions: Array<number> = new Array<number>();

  fractionSizes: Array<any> = new Array<any>();
  selectedSize:any;
  subRef: Subscription;

  fillSizeArray():void {
    if (this.range && this.range.complete) {

      this.fractions.length = 0;
      const step = (this.range ? this.range.step : 0.125);
      for (let i = step; i < 1; i += step) {
        this.fractions.push(i);
      }

      let minWhole = Math.floor(this.range.min < 1 ? 1 : this.range.min);
      let maxWhole = Math.floor(this.range.max);
      let wholeNumbers:number = maxWhole - minWhole + 1;

      this.sizes = new Array<any>(wholeNumbers)
        .fill(0)
        .map((x, i) => {
          return {
            value: i + minWhole
          };
        });

      // add all values < 1 as individual entries
      this.fractionSizes.length = 0;
      let val = this.range.min;
      while (val < 1 && val <= this.range.max) {
        this.fractionSizes.push({
          value: val
        });
        val += this.range.step;
      }
      this.sizes = this.fractionSizes.concat(this.sizes);

      this.selectedSize = this.sizes.find( (size) => {
        return size.value === Math.floor(this.value);
      });

      this.activeSize = this.selectedSize ? this.selectedSize : this.sizes[0];
    } else {
      if (this.sizes) {
        this.sizes.length = 0;
      }
    }
  }

  ngOnInit() {
    this.fillSizeArray();
  }

  ngOnChanges(change) {
    if (change.range) {
      this.range = change.range.currentValue;
      this.fillSizeArray();
    }
  }

  optionHeight:number;
  activeSize:any;
  onScrollFn() {
    if (isPlatformBrowser(this.platformId) && this.select.panel) {
      this.select.panel.nativeElement.removeEventListener('scroll', () => this.onScrollFn());
      this.window.requestAnimationFrame( () => {
        let scrollTop = this.select.panel.nativeElement.scrollTop;
        this.activeSize = this.sizes[scrollTop < 5 ? 0 : Math.ceil(scrollTop / this.optionHeight) - (this.extraOptions ? this.extraOptions.length : 0)];
      });
    }
  };

  ngAfterViewInit() {
    if (this.subRef && !this.subRef.closed) {
      this.subRef.unsubscribe();
    }
    this.subRef = this.select.openedChange.subscribe((open) => {
      if (open) {
        this.optionHeight = (this.select.options.first as any)._element.nativeElement.clientHeight;
        this.select.panel?.nativeElement.addEventListener('scroll', () => this.onScrollFn());

        this.selectedSize = this.sizes.find( (size) => {
          return size.value === (this.value < 1 ? this.value : Math.floor(this.value));
        });

        this.activeSize = this.selectedSize ? this.selectedSize : this.sizes[0];

        let activeSizePos = this.sizes.indexOf(this.activeSize) + (this.extraOptions ? this.extraOptions.length : 0);

        // special case if it matches an extra option use that as position
        if (this.extraOptions) {
          if (this.value === (this.extraOptions[0] ? this.extraOptions[0].value : NaN)) {
            activeSizePos = 0;
          }
          else if (this.value === (this.extraOptions[1] ? this.extraOptions[1].value : NaN)) {
            activeSizePos = 1;
          }
        }

        if (activeSizePos < 3) {
          activeSizePos = 0;
        }

        if (isPlatformBrowser(this.platformId)) {
          requestAnimationFrame(() => {
            this.select.panel.nativeElement.scrollTop = 60 * activeSizePos - 40;
          });
        }
      }
    });
  }

  //The internal data model
  private innerValue: any;

  //Placeholders for the callbacks which are later providesd
  //by the Control Value Accessor
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  //get accessor
  get value(): any {
    return this.innerValue;
  };

  //set accessor including call the onchange callback
  set value(v: any) {
    if (v !== this.innerValue) {
      this.innerValue = v;
      this.selectedSize = this.sizes.find( (size) => {
        return size.value === Math.floor(this.value);
      });
      this.onChangeCallback(v);
    }
  }

  //Set touched on blur
  onBlur() {
    this.onTouchedCallback();
  }

  //From ControlValueAccessor interface
  writeValue(value: any) {
    if (value !== this.innerValue) {
      this.innerValue = value;
    }
  }

  //From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  //From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  selectClose($event) {
    this.onClose.emit($event);
  }

  activate(size:any) {
    this.activeSize = size;
  }

  firstAvailableFractionForSize(size): number {
    return this.fractions.find( (frac) => {
      return (size === 0 || size >= 1) && size + frac >= this.range.min && size + frac <= this.range.max;
    });
  }

  ngOnDestroy() {
    if (this.subRef && !this.subRef.closed) {
      this.subRef.unsubscribe();
    }
  }
}
